import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FiFilm, FiImage, FiLayers, FiPlus, FiUploadCloud } from 'react-icons/fi';
import { PiFilesFill } from 'react-icons/pi';

import type {
  QuickInferenceSessionListItem,
  QuickInferenceSessionStatusFilter,
} from '../../../helpers/api/quick-inference/quick-inference';
import { Button } from '../../ComponnentsUI/common/Button';
import { QuickInferenceFilePreviewCard } from './QuickInferenceFilePreviewCard';
import { QuickInferenceSessionSidebar } from './QuickInferenceSessionSidebar';
import { QuickInferenceUploadProgress } from './QuickInferenceUploadProgress';
import { fileKind, formatFileSize, truncateFilename } from './quickInferenceUtils';
import type { useQuickInferenceUpload } from './useQuickInferenceUpload';

const ACCEPT = 'image/*,video/*,.dcm,application/dicom';

const FORMAT_CHIPS = [
  { icon: FiImage, label: 'Images', color: 'text-emerald-600 bg-emerald-50' },
  { icon: FiFilm, label: 'Videos', color: 'text-violet-600 bg-violet-50' },
  { icon: FiLayers, label: 'DICOM', color: 'text-blue-600 bg-blue-50' },
];

export interface QuickInferenceWorkspaceHandle {
  openFilePicker: () => void;
  scrollIntoView: () => void;
}

type UploadState = ReturnType<typeof useQuickInferenceUpload>;

interface Props extends UploadState {
  sessions: QuickInferenceSessionListItem[];
  sessionsTotal: number;
  sessionsStatusCounts: {
    all: number;
    active: number;
    success: number;
    failed: number;
  };
  sessionsLoading: boolean;
  sessionsLoadingMore: boolean;
  sessionsHasMore: boolean;
  sessionsFilter: QuickInferenceSessionStatusFilter;
  sessionsSearch: string;
  onSessionsFilterChange: (filter: QuickInferenceSessionStatusFilter) => void;
  onSessionsSearchChange: (query: string) => void;
  onSessionsLoadMore: () => void;
  onOpenSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
}

export const QuickInferenceWorkspace = forwardRef<QuickInferenceWorkspaceHandle, Props>(
  function QuickInferenceWorkspace(
    {
      selectedFiles,
      dicomSpacingInfo,
      submitting,
      uploadProgress,
      uploadFileStatuses,
      activeFileName,
      completedCount,
      addFiles,
      removeFileAt,
      fileUid,
      sessions,
      sessionsTotal,
      sessionsStatusCounts,
      sessionsLoading,
      sessionsLoadingMore,
      sessionsHasMore,
      sessionsFilter,
      sessionsSearch,
      onSessionsFilterChange,
      onSessionsSearchChange,
      onSessionsLoadMore,
      onOpenSession,
      onDeleteSession,
    },
    ref
  ) {
    const workspaceRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [mobilePanel, setMobilePanel] = useState<'upload' | 'history'>('upload');

    useImperativeHandle(ref, () => ({
      openFilePicker: () => fileInputRef.current?.click(),
      scrollIntoView: () =>
        workspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    }));

    const handleDrop = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files?.length) {
          addFiles(e.dataTransfer.files);
        }
      },
      [addFiles]
    );

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(true);
    }, []);

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
          addFiles(e.target.files);
        }
        e.target.value = '';
      },
      [addFiles]
    );

    const hasFiles = selectedFiles.length > 0;

    const totalSize = useMemo(
      () => selectedFiles.reduce((sum, file) => sum + file.size, 0),
      [selectedFiles]
    );

    return (
      <div
        ref={workspaceRef}
        className="flex min-h-0 flex-1 flex-col overflow-hidden lg:min-h-[480px] lg:flex-row"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={handleFileInputChange}
        />

        {/* Mobile / tablet tab switcher */}
        <div className="flex shrink-0 border-b border-slate-100 bg-slate-50/80 p-2 dark:border-gray-700 dark:bg-gray-900/40 lg:hidden">
          <div className="flex w-full rounded-lg bg-white p-0.5 ring-1 ring-slate-200 dark:bg-gray-800 dark:ring-gray-600">
            <button
              type="button"
              onClick={() => setMobilePanel('upload')}
              className={`flex-1 rounded-md px-3 py-2 text-xs font-semibold transition ${
                mobilePanel === 'upload'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:text-gray-400'
              }`}
            >
              Upload
            </button>
            <button
              type="button"
              onClick={() => setMobilePanel('history')}
              className={`flex-1 rounded-md px-3 py-2 text-xs font-semibold transition ${
                mobilePanel === 'history'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:text-gray-400'
              }`}
            >
              History{sessionsTotal > 0 ? ` (${sessionsTotal})` : ''}
            </button>
          </div>
        </div>

        {/* Left — history */}
        <div
          className={`flex h-full min-h-0 flex-col border-b border-slate-100 dark:border-gray-700 lg:w-[360px] lg:shrink-0 lg:border-b-0 lg:border-r xl:w-[400px] ${
            mobilePanel === 'history' ? 'min-h-0 flex-1' : 'hidden lg:flex'
          }`}
        >
          <QuickInferenceSessionSidebar
            items={sessions}
            total={sessionsTotal}
            statusCounts={sessionsStatusCounts}
            loading={sessionsLoading}
            loadingMore={sessionsLoadingMore}
            hasMore={sessionsHasMore}
            filter={sessionsFilter}
            search={sessionsSearch}
            onFilterChange={onSessionsFilterChange}
            onSearchChange={onSessionsSearchChange}
            onLoadMore={onSessionsLoadMore}
            onOpen={onOpenSession}
            onDelete={onDeleteSession}
          />
        </div>

        {/* Right — upload */}
        <div
          className={`flex h-full min-h-0 min-w-0 flex-1 flex-col ${
            mobilePanel === 'upload' ? 'flex' : 'hidden lg:flex'
          }`}
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-3 dark:border-gray-700 sm:px-5">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/30">
                <FiUploadCloud size={16} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800 dark:text-white">Upload queue</p>
                <p className="text-[11px] text-slate-500">
                  {hasFiles
                    ? `${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'} · ${formatFileSize(totalSize)}`
                    : 'Add files to start'}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
              className="w-full bg-blue-600 text-white shadow-sm shadow-blue-600/20 sm:w-auto"
            >
              <FiPlus />
              Add files
            </Button>
          </div>

          <div
            className={`relative flex min-h-0 flex-1 flex-col overflow-y-auto p-4 transition-colors sm:p-5 ${
              isDragOver ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'bg-slate-50/50 dark:bg-gray-900/50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
          >
            {!hasFiles ? (
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                }}
                className={`flex min-h-0 flex-1 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-all sm:px-6 sm:py-12 ${
                  isDragOver
                    ? 'scale-[1.01] border-blue-400 bg-blue-50 shadow-inner shadow-blue-100 dark:bg-blue-900/20'
                    : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-md dark:border-gray-600 dark:bg-gray-800/40'
                }`}
              >
                <div
                  className={`mb-4 flex h-16 w-16 items-center justify-center rounded-2xl transition ${
                    isDragOver ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  <PiFilesFill size={32} />
                </div>
                <p className="text-base font-semibold text-slate-700 dark:text-gray-200">
                  Drop files here
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  or <span className="font-semibold text-blue-600">browse</span> from your computer
                </p>

                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {FORMAT_CHIPS.map(({ icon: Icon, label, color }) => (
                    <span
                      key={label}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${color}`}
                    >
                      <Icon size={12} />
                      {label}
                    </span>
                  ))}
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  className="mt-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Choose files
                </Button>
              </div>
            ) : (
              <div className={`space-y-4 ${submitting ? 'pointer-events-none select-none opacity-40' : ''}`}>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`cursor-pointer rounded-xl border border-dashed px-4 py-3 text-center text-xs transition ${
                    isDragOver
                      ? 'border-blue-400 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800'
                  }`}
                >
                  <FiPlus className="mr-1 inline align-text-bottom" />
                  Drop more files or click to browse
                </div>

                <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
                  {selectedFiles.map((file, index) => (
                    <QuickInferenceFilePreviewCard
                      key={fileUid(file, index)}
                      file={file}
                      onRemove={() => removeFileAt(index)}
                      disabled={submitting}
                    />
                  ))}
                </div>

                {dicomSpacingInfo.length > 0 ? (
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      DICOM pixel spacing
                    </p>
                    <div className="space-y-1">
                      {dicomSpacingInfo.map((info) => (
                        <div
                          key={info.uid}
                          className="flex items-center justify-between gap-2 text-xs"
                        >
                          <span
                            className="truncate text-slate-600 dark:text-gray-300"
                            title={info.file.name}
                          >
                            {truncateFilename(info.file.name, 36)}
                          </span>
                          <span className="shrink-0 font-medium text-slate-500">
                            {info.fromDicom && info.spacing != null
                              ? `${info.spacing} mm`
                              : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                    <span className="font-medium text-slate-700 dark:text-gray-200">
                      Queue summary
                    </span>
                    {selectedFiles.map((file) => (
                      <span
                        key={`${file.name}-${file.size}`}
                        className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase dark:bg-gray-700"
                      >
                        {fileKind(file)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {submitting ? (
              <QuickInferenceUploadProgress
                progress={uploadProgress}
                completedCount={completedCount}
                totalCount={uploadFileStatuses.length || selectedFiles.length}
                activeFileName={activeFileName}
                files={uploadFileStatuses}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  }
);
